import React, { useState, useEffect } from "react";

export default function UpdateModal({ onClose, onSave, field, currentValue }) {
  // --- Name fields ---
  const [firstName, setFirstName] = useState(
    field === "Name Surname" && typeof currentValue === "string"
      ? currentValue.split(" ")[0]
      : ""
  );
  const [lastName, setLastName] = useState(
    field === "Name Surname" && typeof currentValue === "string"
      ? currentValue.split(" ").slice(1).join(" ")
      : ""
  );

  // --- Generic single-value field (Email, Phone, Password, etc.) ---
  const [value, setValue] = useState(
    typeof currentValue === "string" ? currentValue : ""
  );

  // --- Address fields (backend contract) ---
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateField, setStateField] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // --- Phone fields ---
  const [phoneCountryCode, setPhoneCountryCode] = useState("1"); // default US
  const [phoneLocal, setPhoneLocal] = useState("");
  const countryCodes = [
    { code: "1", label: "US/CA (+1)" },
    { code: "44", label: "UK (+44)" },
    { code: "91", label: "IN (+91)" },
    { code: "61", label: "AU (+61)" },
    { code: "49", label: "DE (+49)" },
    { code: "33", label: "FR (+33)" },
    { code: "90", label: "TR (+90)" }
  ];

  // --- Email verification (your original logic kept) ---
  const [step, setStep] = useState(1); // 1 = Enter Email, 2 = Enter OTP
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);

  // --- Password change fields ---
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Init for Name + Address
  useEffect(() => {
    if (field === "Name Surname" && typeof currentValue === "string") {
      const parts = currentValue.split(" ");
      setFirstName(parts[0] || "");
      setLastName(parts.slice(1).join(" ") || "");
    }
    if (field === "Address" && currentValue && typeof currentValue === "object") {
      setAddressLine1(currentValue.address_line1 || currentValue.addressLine1 || "");
      setAddressLine2(currentValue.address_line2 || currentValue.addressLine2 || "");
      setCity(currentValue.city || currentValue.district || "");
      setStateField(currentValue.state || currentValue.province || "");
      setCountry(currentValue.country || "");
      setPostalCode(currentValue.postal_code || currentValue.postalCode || "");
    }
  }, [field, currentValue]);

  // OTP countdown
  useEffect(() => {
    let countdown;
    if (field === "E-Mail Address" && step === 2 && timer > 0) {
      countdown = setInterval(() => setTimer((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    }
    return () => clearInterval(countdown);
  }, [field, step, timer]);

  // Email: Send verification code
  const handleNext = async () => {
    try {
      const res = await fetch("/api/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value }),
      });
      if (!res.ok) throw new Error("Failed to send verification code");
      setStep(2);
      setTimer(30);
    } catch (error) {
      alert(error.message);
    }
  };

  // Email: Verify code then save
  const handleVerify = async () => {
    const enteredCode = code.join("");
    try {
      const res = await fetch("/api/verify-email-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value, code: enteredCode }),
      });
      if (!res.ok) throw new Error("Verification request failed");
      const result = await res.json();
      if (result.success) onSave(value);
      else alert("Invalid Code");
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSaveClick = () => {
    if (field === "Name Surname") {
      onSave(`${firstName} ${lastName}`.trim());
    } else if (field === "Phone Number") {
      const digits = (phoneLocal || value || "").replace(/\D/g, "");
      if (!digits) {
        alert("Please enter a phone number");
        return;
      }
      const e164 = `+${phoneCountryCode}${digits}`;
      onSave(e164);
    } else if (field === "Password") {
      if (!currentPassword) {
        alert("Please enter your current password");
        return;
      }
      if (!newPassword) {
        alert("Please enter a new password");
        return;
      }
      onSave({ current_password: currentPassword, new_password: newPassword });
    } else if (field === "E-Mail Address") {
      if (step === 1) handleNext();
      else handleVerify();
    } else if (field === "Address") {
      onSave({
        address_line1: addressLine1,
        address_line2: addressLine2,
        city,
        state: stateField,
        country,
        postal_code: postalCode
      });
    } else {
      onSave(value);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl shadow-lg p-6 w-[460px]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">
            {field === "E-Mail Address" && step === 2
              ? "Enter Verification Code"
              : "Change Information"}
          </h2>
          <button onClick={onClose} className="text-gray-500 text-xl">&times;</button>
        </div>

        {/* Fields */}
        <div className="space-y-4">
          {field === "Name Surname" ? (
            <>
              <div>
                <p className="text-xs text-gray-500 mb-1">First Name</p>
                <input
                  className="w-full border rounded-md p-2 text-sm"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Last Name</p>
                <input
                  className="w-full border rounded-md p-2 text-sm"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </>
          ) : field === "Address" ? (
            <>
              <div>
                <p className="text-xs text-gray-500 mb-1">Address Line 1</p>
                <input className="w-full border rounded-md p-2 text-sm" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Address Line 2</p>
                <input className="w-full border rounded-md p-2 text-sm" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">City</p>
                  <input className="w-full border rounded-md p-2 text-sm" value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">State / Province</p>
                  <input className="w-full border rounded-md p-2 text-sm" value={stateField} onChange={(e) => setStateField(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Country</p>
                  <input className="w-full border rounded-md p-2 text-sm" value={country} onChange={(e) => setCountry(e.target.value)} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Postal Code</p>
                  <input className="w-full border rounded-md p-2 text-sm" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                </div>
              </div>
            </>
          ) : field === "E-Mail Address" && step === 2 ? (
            <>
              <div className="flex justify-center gap-2">
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    maxLength={1}
                    className="w-10 h-10 border rounded text-center text-lg"
                    value={digit}
                    onChange={(e) => {
                      const next = [...code];
                      next[idx] = e.target.value;
                      setCode(next);
                    }}
                  />
                ))}
              </div>
              <p className="text-center text-xs text-gray-500">
                Request new code ({timer}s)
              </p>
            </>
          ) : field === "Phone Number" ? (
            <div>
              <p className="text-xs text-gray-500 mb-1">Phone Number</p>
              <div className="flex gap-2">
                <select
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  className="w-28 border rounded-md p-2 text-sm"
                >
                  {countryCodes.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                <input
                  className="flex-1 border rounded-md p-2 text-sm"
                  value={phoneLocal}
                  onChange={(e) => setPhoneLocal(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
              <p className="mt-1 text-[11px] text-gray-500">We will submit in international format (E.164), e.g. +15550123.</p>
            </div>
          ) : field === "Password" ? (
            <div>
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-1">Current password</p>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    className="w-full border rounded-md p-2 pr-10 text-sm"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <span
                    className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
                    onClick={() => setShowCurrentPassword((v) => !v)}
                    title={showCurrentPassword ? "Hide" : "Show"}
                  >
                    <img src="icons/eye-line.svg" alt="toggle" className="h-4 w-4" />
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">New password</p>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="w-full border rounded-md p-2 pr-10 text-sm"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <span
                    className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
                    onClick={() => setShowNewPassword((v) => !v)}
                    title={showNewPassword ? "Hide" : "Show"}
                  >
                    <img src="icons/eye-line.svg" alt="toggle" className="h-4 w-4" />
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-gray-500"></p>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs text-gray-500 mb-1">{field}</p>
              <input
                className="w-full border rounded-md p-2 text-sm"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                type={field === "Password" ? "password" : "text"}
                placeholder={field === "Phone Number" ? "Enter phone number" : ""}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between mt-6">
          <button onClick={onClose} className="text-sm">Cancel</button>
          <button
            onClick={handleSaveClick}
            className="bg-gray-900 text-white rounded-md px-4 py-2 text-sm"
          >
            {field === "E-Mail Address" && step === 1 ? "Next" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
